<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMeusDadosStore } from '@/stores/meusDados'
import { useNotificationStore } from '@/stores/notification'

const router = useRouter()
const route = useRoute()
const meusDadosStore = useMeusDadosStore()
const notificationStore = useNotificationStore()

// Form data
const dados = ref({
  nome: '',
  email: '',
  foto: '',
  situacao: false,
  datanascimento: '',
  dataadmissao: '',
  datademissao: '',
  cpf: '',
  freela: false,
  celular1: '',
  celular2: '',
  endereco: '',
  cnpj: '',
  razaosocial: '',
  subdominio: '',
  site_parceiro: false,
  perfil: '',
  senha_atual: '',
  senha: '',
  senha_confirmation: '',
});

// UI state
const loading = ref(false);
const error = ref('');
const validationErrors = ref({});
const formValidation = ref(null);

// regras de validação
const nomeRules = [v => !!v || 'Nome é obrigatório'];
const emailRules = [v => !!v || 'E-mail é obrigatório'];

// regras de validação para alteração de senha
const senhaRules = [
  v => !v || v.length >= 8 || 'A senha deve ter no mínimo 8 caracteres',
  v => {
    if (v && v !== dados.value.senha_confirmation) {
      return 'As senhas não coincidem';
    }
    return true;
  }
];

const senhaConfirmationRules = [
  v => {
    if (dados.value.senha && !v) {
      return 'Confirmação de senha é obrigatória';
    }
    if (dados.value.senha && v !== dados.value.senha) {
      return 'As senhas não coincidem';
    }
    return true;
  }
];

// senha_atual só é necessário se senha for preenchida
const senhaAtualRules = computed(() => {
  if (dados.value.senha) {
    return [v => !!v || 'Senha atual é obrigatória'];
  }
  return [];
});

onMounted(async () => {
  //buscar dados usuario logado
  loading.value = true;
  try{
    const fetchedPerfil = await meusDadosStore.fetchMeusDados()
    dados.value = fetchedPerfil || dados.value
  }catch(error){
    error.value = 'Erro ao buscar dados do usuário';
  }finally{
    loading.value = false;
  }
})

// limpar erros de validação
const clearValidationError = (field) => {
  if (validationErrors.value[field]) {
    delete validationErrors.value[field];
  }
};

const salvarDados = async () => {
  error.value = '';
  validationErrors.value = {};
  
  // Basic form validation first
  const valid = await formValidation.value?.validate();
  if (!valid.valid) {
    return;
  }

  // Check if passwords match when changing password
  if (dados.value.senha && dados.value.senha !== dados.value.senha_confirmation) {
    validationErrors.value.senha = ['As senhas não coincidem'];
    return;
  }

  loading.value = true;
  try {
    const result = await meusDadosStore.updateMeusDados(dados.value);
    
    if (result.success) {
      notificationStore.showNotification('Dados atualizados com sucesso!', 'success');
      
      // limpar campos de senha
      dados.value.senha_atual = '';
      dados.value.senha = '';
      dados.value.senha_confirmation = '';
    } else {
      // Handle validation errors
      if (result.status === 422 && result.errors) {
        validationErrors.value = result.errors;
        error.value = result.message || 'Verifique os campos destacados';
      } else {
        error.value = result.message || 'Erro ao salvar dados!';
      }
    }
  } catch (err) {
    error.value = 'Erro ao salvar dados!';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <v-card class="elevation-12">
    <v-card-title class="text-left">
      <h1 class="text-h5 mt-4">Editar Meus dados</h1>
    </v-card-title>

    <v-card-text>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        closable
        class="mb-4"
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-form @submit.prevent="salvarDados" ref="formValidation">
        <v-row>
          <v-col cols="12" md="12">
            <v-text-field hide-details="auto" v-model="dados.foto" label="Foto"></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field 
              hide-details="auto" 
              v-model="dados.nome" 
              :rules="nomeRules" 
              label="Nome" 
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field 
              hide-details="auto" 
              v-model="dados.email" 
              :rules="emailRules" 
              label="E-mail" 
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="dados.endereco" label="Endereço completo"></v-text-field>
          </v-col>          

          <v-col cols="12" md="3">
            <v-text-field
              v-model="dados.celular1"
              label="Celular Pessoal"
              v-mask="'(##) #####-####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="dados.celular2"
              label="Celular de Contato"
              v-mask="'(##) #####-####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="dados.cpf"
              label="CPF"
              v-mask="'###.###.###-##'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="dados.datanascimento"
              label="Data de Nascimento"
              v-mask="'##/##/####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="dados.cnpj"
              label="CNPJ"
              v-mask="'##.###.###/####-##'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="dados.razaosocial" label="Razão Social"></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="dados.subdominio" label="Subdomínio"></v-text-field>
          </v-col>

          <!-- Password section with improved validation -->
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <h3 class="text-subtitle-1 mb-3">Alteração de Senha</h3>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="dados.senha_atual"
              label="Senha Atual"
              type="password"
              :rules="senhaAtualRules"
              :error-messages="validationErrors.senha_atual"
              @input="clearValidationError('senha_atual')"
              hint="Apenas preencha se deseja alterar sua senha"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="dados.senha"
              label="Nova Senha"
              type="password"
              :rules="senhaRules"
              :error-messages="validationErrors.senha"
              @input="clearValidationError('senha')"
              hint="Mínimo de 8 caracteres"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="dados.senha_confirmation"
              label="Confirmar Nova Senha"
              type="password"
              :rules="senhaConfirmationRules"
              :error-messages="validationErrors.senha_confirmation"
              @input="clearValidationError('senha_confirmation')"
            ></v-text-field>
          </v-col>
        </v-row>
          
        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-btn 
              color="primary" 
              block 
              size="large" 
              @click="salvarDados" 
              :loading="loading"
              :disabled="loading"
            >
              Salvar
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn 
              variant="text" 
              color="primary" 
              block 
              @click="router.back()"
              :disabled="loading"
            >
              Cancelar
            </v-btn>
          </v-col>
        </v-row>
        
      </v-form>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.v-text-field--error {
  margin-bottom: 0.5rem;
}
</style>
